import React, { useCallback, useContext, useEffect } from "react";
import * as am5 from "@amcharts/amcharts5";
import i18next from "i18next";
import { formatNumber } from "../../../services/common/Utils";
import EAPieChart from "../../../components/common/charts/EAPieChart";
import { hidePieSliceLabel, mapDepartmentsFilters } from "../../../services/common/ChartUtils";
import { Context } from "../../../states/Store";
import { getAccountThirdpartyStates } from "../../../services/edge/AccountService";
import ROUTES from "../../../config/Routes";
import { ThirdPartyBlocSkeleton } from "components/common/skeleton/SkeletonDashboard";
import { EANoData } from "components/common/EANoData";
import useSelectedAccountId from "components/common/hooks/useSelectedAccountId";
import { useRef } from "react";
import { ERROR } from "services/common/Constants";
import { isAbortError } from "services/common/ApiUtils";
import _ from 'lodash';

const states = {
  subscribed: {
    color: "#60D273",
    order: 1
  },
  invited: {
    color: "#377DFF",
    order: 2
  },
  without_contact: {
    color: "#C4C4C4",
    order: 4
  },
  declined: {
    color: "#7A7A7A",
    order: 5
  },
};

function TpStatesChart({ getFilteredGraphFilter = () => {}}) {
  const [state, dispatch] = useContext(Context);
  const [thirdpartiesStatesCount, setThirdpartiesStatesCount] = React.useState();
  const [mappedStates, setMappedStates] = React.useState();
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasTPs, setHasTPs] = React.useState(false);
  const accountId = useSelectedAccountId();

  const abortCtrlRef = useRef();

  // Reset values on account change
  useEffect(() => {
    setThirdpartiesStatesCount(undefined);
    setMappedStates(undefined);
  }, [accountId])

  const fetchTpStates = useCallback(
    (queryObjCountry, queryObjDepartment) => {

      if(abortCtrlRef.current) {
        abortCtrlRef.current.abort();
      }
  
      abortCtrlRef.current = new AbortController();
      const signal = abortCtrlRef.current.signal;

      if (accountId && state.account) {
        (async () => {
          try {
            setIsLoading(true);
            let resCountry = {};
            let resDepartment = {};
            let res = {};

            if (!_.isEmpty(queryObjCountry)) {
              resCountry = await getAccountThirdpartyStates(
                accountId,
                queryObjCountry,
                null,
                signal
              );
            }
            if (!_.isEmpty(queryObjDepartment)) {
              resDepartment = await getAccountThirdpartyStates(
                accountId,
                queryObjDepartment,
                null,
                signal
              );
            }

            if (_.isEmpty(queryObjCountry) && _.isEmpty(queryObjDepartment)) {
              res = await getAccountThirdpartyStates(
                accountId,
                {},
                null,
                signal
              );
            }else {
               res = {
                ...resCountry,
                ...resDepartment,
                content: [
                  ...(resCountry?.content?.length > 0 ? resCountry.content : []),
                  ...(resDepartment?.content?.length > 0 ? resDepartment.content : [])
                ]
              };
            }

            const total = res.content
              ?.map((d) => d.count)
              .reduce((partialSum, a) => partialSum + a, 0);

            setIsLoading(false)
            setHasTPs(total > 0)

            setThirdpartiesStatesCount(_.uniqWith(res.content, _.isEqual));
          } catch (e) {
            if(!isAbortError(e)) {
              dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
            } 
          }
        })();
      }
    },
    [accountId, dispatch, state.account]
  );

  // Map counts to match pie data structure
  useEffect(() => {
    if(thirdpartiesStatesCount) {
      const total = thirdpartiesStatesCount
      ?.map((d) => d.count)
      .reduce((partialSum, a) => partialSum + a, 0);
  
      setMappedStates({
        data: thirdpartiesStatesCount?.map((state) => ({
          id: state.registrationStatus,
          itemLabel: i18next.t( `dashboard.thirdparties.indicator.${state.registrationStatus}`),
          type: "thirdparties",
          value: state.count,
          formattedValue: formatNumber(state.count),
          settings: {
            fill: am5.color(states[state.registrationStatus].color),
          },
          labelSettings: {
            forceHidden: hidePieSliceLabel(state.count, total),
          },
        }))
        .sort((a, b) => (states[a.id].order > states[b.id].order ? 1 : -1)),
        total: formatNumber(total)
      });
    }
  }, [thirdpartiesStatesCount])

  const mixedRequest = (graphFilters) => {
    return (
      graphFilters?.map?.some((data) => data.id.includes('FR-')) &&
      graphFilters?.map?.some((data) => data.keyLabel === i18next.t('dashboard.filters.country'))
    );
  }

  // Build filters query from graph filters
  useEffect(() => {
    let queryObjCountry = {};
    let queryObjDepartment = {};
    if (getFilteredGraphFilter()?.map?.length > 0) {
      const isCountry =
        getFilteredGraphFilter().map[0].keyLabel ===
        i18next.t("dashboard.filters.country");

      if (mixedRequest(getFilteredGraphFilter())) {
        queryObjCountry.countries = getFilteredGraphFilter().map.map((x) => x.id);
        queryObjDepartment.departments = mapDepartmentsFilters(getFilteredGraphFilter().map).map((x) => x.id);
        if (getFilteredGraphFilter()?.treeMap?.length > 0) {
          queryObjCountry.majorCodes = getFilteredGraphFilter().treeMap.map((x) => x.id);
          queryObjDepartment.majorCodes = getFilteredGraphFilter().treeMap.map((x) => x.id);
        }
      }else if (isCountry) {
        queryObjCountry.countries = getFilteredGraphFilter().map.map((x) => x.id);
        if (getFilteredGraphFilter()?.treeMap?.length > 0) {
          queryObjCountry.majorCodes = getFilteredGraphFilter().treeMap.map((x) => x.id);
        }
      } else {
        queryObjDepartment.departments = mapDepartmentsFilters(getFilteredGraphFilter().map).map((x) => x.id);
        if (getFilteredGraphFilter()?.treeMap?.length > 0) {
          queryObjDepartment.majorCodes = getFilteredGraphFilter().treeMap.map((x) => x.id);
        }
      }
    }
    fetchTpStates(queryObjCountry, queryObjDepartment);

    return () => {
      abortCtrlRef.current?.abort();
    }
  }, [fetchTpStates, state.graphFilters?.map, state.graphFilters?.treeMap, getFilteredGraphFilter]);

  return (
    !isLoading ? (
      <>
        {hasTPs ?
          <EAPieChart
            id="thirdpartiesStates"
            data={mappedStates}
            tab={ROUTES.THIRDPARTIES}
          /> :
          <EANoData
            style={{ width:'100%', height: 'calc(100% - 30px)'}}
            label={i18next.t("dashboard.thirdparties.nodata")}
            icon="noThirdparties"
          />
        }
      </>
    ) : <ThirdPartyBlocSkeleton />
  )
}

export default TpStatesChart
